import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../../api';

// Consulta independente do formulário: somente as rotas de leitura existentes.
export function useConsultaDocumentosModule({ eleicaoUuid, permissoes }) {
    const [lista, setLista] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [abrindo, setAbrindo] = useState(false);
    const [erro, setErro] = useState('');
    const [preview, setPreview] = useState(null);
    const contexto = useRef(0), consulta = useRef(0), trava = useRef(false), urlRef = useRef(null);
    const base = `/eleicao/${eleicaoUuid}/documentos`;
    const fecharPreview = useCallback(() => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
        setPreview(null);
    }, []);
    const carregar = useCallback(async () => {
        if (!eleicaoUuid || !permissoes.autorizado) return;
        const atual = ++consulta.current;
        const ctx = contexto.current;
        setCarregando(true); setErro('');
        try {
            const { data } = await api.get(base);
            if (ctx !== contexto.current || atual !== consulta.current) return;
            if (!Array.isArray(data)) throw new Error('Lista de documentos inválida.');
            setLista(data);
        } catch (e) {
            if (ctx === contexto.current && atual === consulta.current) setErro(e.response?.data?.message || e.message);
        } finally {
            if (ctx === contexto.current && atual === consulta.current) setCarregando(false);
        }
    }, [base, eleicaoUuid, permissoes.autorizado]);
    useEffect(() => {
        contexto.current += 1; consulta.current += 1; trava.current = false;
        setLista([]); setErro(''); setCarregando(false); setAbrindo(false); fecharPreview();
        carregar();
        return () => {
            contexto.current += 1; consulta.current += 1;
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        };
    }, [carregar, fecharPreview, permissoes.imprimir]);
    const abrir = async (arquivo, { baixar = false, novaAba = false } = {}) => {
        if (trava.current || carregando || !eleicaoUuid || !permissoes.autorizado || !permissoes.imprimir) return;
        if (!arquivo?.documentoUuid || !['emitido', 'assinado'].includes(arquivo.tipo)) return;
        const ctx = contexto.current;
        trava.current = true; setAbrindo(true); setErro('');
        let janela = null;
        try {
            // No celular a aba precisa ser reservada ainda no clique, antes do await.
            if (novaAba && !baixar) {
                try {
                    janela = window.open('about:blank', '_blank');
                    if (janela) {
                        janela.opener = null;
                        janela.document.title = 'Carregando PDF';
                        janela.document.body.textContent = 'Carregando documento...';
                    }
                } catch { if (janela) janela.close(); janela = null; }
            }
            const { data } = await api.get(`${base}/${arquivo.documentoUuid}/pdf/${arquivo.tipo}`, { responseType: 'blob' });
            if (ctx !== contexto.current) return;
            const blob = new Blob([data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            if (baixar) {
                const link = document.createElement('a');
                link.href = url; link.download = arquivo.nome;
                document.body.appendChild(link); link.click(); link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } else {
                fecharPreview(); urlRef.current = url;
                setPreview({ url, nome: arquivo.nome });
                if (janela && !janela.closed) {
                    try { janela.location.replace(url); janela = null; }
                    catch { /* O diálogo oferece abertura manual e download. */ }
                }
            }
        } catch (e) {
            let mensagem = e.response?.data?.message || e.message || 'Erro ao abrir documento.';
            if (e.response?.data instanceof Blob) {
                try { mensagem = JSON.parse(await e.response.data.text()).message || mensagem; } catch { /* Mantém o erro original. */ }
            }
            if (ctx === contexto.current) setErro(mensagem);
        } finally {
            if (janela && !janela.closed) janela.close();
            if (ctx === contexto.current) { trava.current = false; setAbrindo(false); }
        }
    };
    return { lista, carregando, erro, preview, ocupado: carregando || abrindo, carregar, abrir, fecharPreview };
}
